import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { X } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useChangePasswordMutation } from "@/redux/services/auth/authApi";
import { toast } from "sonner";

interface ChangePasswordModalProps {
  show: boolean;
  onClose: () => void;
}

export default function ChangePasswordModal({
  show,
  onClose,
}: ChangePasswordModalProps) {
  const isMobile = useIsMobile();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters long";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const res = await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      }).unwrap();

      if (res?.success === true) {
        toast.success(res?.message || "Password changed successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setErrors({});
        onClose();
      } else {
        toast.error(
          res?.message || "Failed to change password. Please try again."
        );
      }
    } catch (error: any) {
      console.error("Password change failed:", error);
      toast.error(
        error?.data?.message || "Failed to change password. Please try again."
      );
    }
  };

  const handleClose = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({});
    onClose();
  };

  const formContent = (
    <div className="space-y-6">
      {/* Form Fields */}
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Password
          </label>
          <Input
            id="current-password"
            name="currentPassword"
            type="password"
            placeholder="Enter current password"
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              if (errors.currentPassword) {
                setErrors((prev) => ({ ...prev, currentPassword: undefined }));
              }
            }}
            className={`w-full h-12 rounded-lg ${
              errors.currentPassword
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-primary focus:ring-primary"
            }`}
          />
          {errors.currentPassword && (
            <p className="text-xs text-red-500 mt-1">
              {errors.currentPassword}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Password
          </label>
          <Input
            id="new-password"
            name="newPassword"
            type="password"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (errors.newPassword) {
                setErrors((prev) => ({ ...prev, newPassword: undefined }));
              }
            }}
            className={`w-full h-12 rounded-lg ${
              errors.newPassword
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-primary focus:ring-primary"
            }`}
          />
          {errors.newPassword && (
            <p className="text-xs text-red-500 mt-1">{errors.newPassword}</p>
          )}
          {!isMobile && !errors.newPassword && (
            <p className="text-xs text-gray-500 mt-2">
              8 characters, including a mix of letters, numbers, and symbols.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password
          </label>
          <Input
            id="confirm-password"
            name="confirmPassword"
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (errors.confirmPassword) {
                setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
              }
            }}
            className={`w-full h-12 rounded-lg ${
              errors.confirmPassword
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-primary focus:ring-primary"
            }`}
          />
          {errors.confirmPassword && (
            <p className="text-xs text-red-500 mt-1">
              {errors.confirmPassword}
            </p>
          )}
          {!isMobile && !errors.confirmPassword && (
            <p className="text-xs text-gray-500 mt-2">
              Re-enter your new password to confirm.
            </p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {isMobile ? (
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full bg-primary hover:bg-primary/90 text-white h-12 text-base font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? "Changing..." : "Change Password"}
        </Button>
      ) : (
        <div className="flex justify-end space-x-3 pt-4">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-primary hover:bg-primary/90 text-white px-5 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Changing..." : "Save Changes"}
          </Button>
        </div>
      )}
    </div>
  );

  if (!show) return null;

  return (
    <Drawer
      open={show}
      onOpenChange={(open) => !open && handleClose()}
      direction={isMobile ? "bottom" : "right"}
    >
      <DrawerContent className={isMobile ? "max-h-[80vh]" : "max-w-sm"}>
        <DrawerHeader>
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-xl font-semibold text-gray-900">
              Change Password
            </DrawerTitle>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </DrawerHeader>

        <div className="px-4 pb-4">{formContent}</div>
      </DrawerContent>
    </Drawer>
  );
}
